<template>
    <div class="@container">
        <div class="-mx-4 -mb-4 p-4 bg-gray-50 w-max min-w-full">
            <!--
              `sticky` makes this a stacking context, so the dropdowns inside cannot rise above the timeline
              on their own: `z-30` lifts the whole toolbar over the timeline's panes instead.
            -->
            <div class="flex flex-row justify-between items-end sticky left-0 z-30 w-[100cqw]">
                <div v-if="!timelineLoading" class="flex flex-row items-end gap-4">
                    <TimelineIntervalChanger
                        v-if="startDateTime"
                        :current-quarter="startDateTime.quarter as QuarterNumbers"
                        :current-year="startDateTime.year"
                        :minimal-year="MINIMAL_YEAR"
                        :maximal-year="MAXIMAL_YEAR"
                        :warning-no-allocations="warningNoAllocations"
                        :is-time-span-modified="isTimeSpanModified"
                        @change-quarter="onChangeQuarter"
                        @change-year="onChangeYear"
                        @click-previous-quarter="onClickPreviousQuarter"
                        @click-next-quarter="onClickNextQuarter"
                    />

                    <OptionSwitch
                        name="timeline-locations-display"
                        :active-index="activeIndex"
                        :label="$t('timeline-locations-display-label')"
                        @change-option="(index) => setActiveIndex(index)"
                    >
                        <OptionSwitchEntry
                            v-for="entry of timelineOptionSwitchEntries"
                            :key="entry.id"
                            :icon="entry.icon"
                            :index="entry.index"
                            :label="entry.label"
                        />
                    </OptionSwitch>
                </div>

                <VariantSelectionDropdown
                    v-if="!timelineLoading && mappedCurrentVariant"
                    :drop-down-status="dropDownStatus"
                    :variants-ref="variantsRef"
                    :visible-variants="visibleVariantIds"
                    :default-active-variants="defaultActiveVariants"
                    :selection-panel-variants="selectionPanelVariants"
                    :highlighted-variant="highlightedVariant"
                    :highlighted-variant-id="highlightedVariantId"
                    :mapped-current-variant="mappedCurrentVariant"
                    :allocation-type="allocationType"
                    :object-id="eventId"
                    :new-variant-index="newVariantIndex"
                    category-color="Event"
                    :on-change-visibility="updateVisibility"
                    :set-highlighted-variant-id="setHighlightedVariantIdById"
                    :on-delete-filter="onDeleteFilter"
                    :on-create-new-variant="onCreateNewVariant"
                    :on-copy-allocations="onCopyAllocations"
                />
            </div>

            <Message v-if="timelineLoading" status="info">
                {{ $t('loading-timeline-data') }}
            </Message>

            <Message
                v-if="!timelineLoading && timelineError"
                status="error"
                :headline="$t('error-fetching-timeline-data')"
            >
                {{ timelineError }}
            </Message>

            <Timeline
                v-if="!timelineLoading && startDateTime && endDateTime && editableVariant && site"
                class="mt-4"
                scroll-container="Page"
                :header-name="timelineHeaderName"
                :header-link-target="timelineHeaderLinkTarget"
                :start-date-time="startDateTime"
                :end-date-time="endDateTime"
                :previous-month-button="previousMonthButton"
                :next-month-button="nextMonthButton"
                :conflicts-by-day="conflictsByDay"
                :site-conflicts-count="siteConflictsCount"
                :update-allocation="updateAllocation"
                @delete-allocation="handleDeleteAllocation"
                @expand-to-previous-month="onExpandToPreviousMonth"
                @expand-to-next-month="onExpandToNextMonth"
            >
                <template #first-column-rows>
                    <ReferenceAllocationFirstColumnCell v-bind="referenceAllocationFirstColumnCell" />
                    <ExternalEventFirstColumnCell v-for="row in firstColumnCells" v-bind="row" :key="row.rowId" />

                    <!-- Sections View -->
                    <template v-if="timelineDisplay === 'sections'">
                        <template v-for="section in sections" :key="section.id">
                            <SectionFirstColumnCell
                                v-bind="section"
                                :section-id="section.id"
                                :conflicts-count="section.conflictsCount"
                                :is-expanded="section.isExpanded"
                                :icon="section.icon"
                                @toggle-section-expanded="toggleSectionExpanded(section.id)"
                            >
                                <template #label="{ labelId }">
                                    <SectionAllocationCountLabel
                                        :id="labelId"
                                        :label="section.label"
                                        :allocation-count="section.allocationCount"
                                    />
                                </template>
                            </SectionFirstColumnCell>
                            <!-- v-show to only visually hide locations when the section is collapsed. This allows the checkbox registration to work correctly. -->
                            <AllocationFirstColumnCell
                                v-for="location in section.locations"
                                v-show="section.isExpanded"
                                v-bind="location"
                                :key="location.id"
                                :is-visible="section.isExpanded"
                                :location-id="location.id"
                                :row-id="`location-${location.id}`"
                                :expandable="location.expandable"
                                :link-target="location.linkTarget"
                                :level="location.level"
                                :icon="location.icon"
                            >
                                <template #label="{ labelId, countTextColorClass }">
                                    <LocationAllocationCountLabel
                                        :id="labelId"
                                        :label="location.label"
                                        :allocation-count="location.allocationCount"
                                        :count-text-color-class="countTextColorClass"
                                    />
                                </template>

                                <template #link-tooltip-header>
                                    {{ location.label }}
                                </template>

                                <template #link-tooltip-text>
                                    {{ $t('open-detail-page') }}
                                </template>
                            </AllocationFirstColumnCell>
                        </template>
                    </template>

                    <!-- Hierarchy View -->
                    <template v-else>
                        <AllocationFirstColumnCell
                            v-for="location in hierarchyRows"
                            v-show="location.isVisible"
                            :key="location.id"
                            :is-visible="location.isVisible"
                            :location-id="location.id"
                            :type-category="location.typeCategory"
                            :parent-id="location.parentId"
                            :row-id="`location-${location.id}`"
                            :expandable="location.expandable"
                            :link-target="location.linkTarget"
                            :level="location.level"
                            :icon="location.icon"
                            @toggle-location-expanded="toggleLocationExpanded(location.id)"
                        >
                            <template #label="{ labelId, countTextColorClass }">
                                <LocationAllocationCountLabel
                                    :id="labelId"
                                    :label="location.label"
                                    :allocation-count="location.allocationCount"
                                    :count-text-color-class="countTextColorClass"
                                />
                            </template>

                            <template #link-tooltip-header>
                                {{ location.label }}
                            </template>

                            <template #link-tooltip-text>
                                {{ $t('open-detail-page') }}
                            </template>
                        </AllocationFirstColumnCell>
                    </template>
                </template>

                <template #days-rows>
                    <LayoutRow v-bind="referenceAllocationsLayoutRow">
                        <Allocation
                            v-for="allocation in referenceAllocations"
                            v-bind="allocation"
                            :key="allocation.id"
                            :allocation-usages="usages"
                            :phase-type-priorities="phaseTypePriorities"
                        />
                    </LayoutRow>

                    <LayoutRow v-for="row in layoutRows" v-bind="row" :key="row.rowId">
                        <Allocation
                            v-for="allocation in row.allocations"
                            v-bind="allocation"
                            :key="allocation.id"
                            :allocation-usages="usages"
                            :phase-type-priorities="phaseTypePriorities"
                        >
                            <template #tooltip-header>
                                <div class="w-full text-center">{{ allocation.tooltipHeader }}</div>
                            </template>
                            <template #tooltip-text>
                                <InternationalTooltip
                                    v-if="allocation.tooltipType === 'International'"
                                    :headline="allocation.tooltipHeadline"
                                />
                                <FederalStatesTooltip
                                    v-if="allocation.tooltipType === 'National'"
                                    :headline="allocation.tooltipHeadline"
                                    :federal-states="allocation.federalStates"
                                />
                            </template>
                        </Allocation>
                    </LayoutRow>

                    <!-- Sections View -->
                    <template v-if="timelineDisplay === 'sections'">
                        <template v-for="section in eventAllocationLayoutRowsBySection" :key="section.id">
                            <SectionDaysRow
                                v-bind="section"
                                @toggle-section-expanded="toggleSectionExpanded(section.id)"
                            />
                            <template v-if="section.isExpanded">
                                <LayoutRow
                                    v-for="row in section.locationRows"
                                    v-bind="row"
                                    :key="row.rowId"
                                    @create-allocation="
                                        (payload) => handleRowCreate(row.locationId, row.locationTypeCategory, payload)
                                    "
                                >
                                    <Allocation
                                        v-for="allocation in row.allocations"
                                        v-bind="allocation"
                                        :key="allocation.id"
                                        :allocation-usages="usages"
                                        :phase-type-priorities="phaseTypePriorities"
                                    />
                                </LayoutRow>
                            </template>
                        </template>
                    </template>

                    <!-- Hierarchy View -->
                    <template v-else>
                        <LayoutRow
                            v-for="row in hierarchyLayoutRows"
                            v-bind="row"
                            :key="row.rowId"
                            @create-allocation="
                                (payload) => handleRowCreate(row.locationId, row.locationTypeCategory, payload)
                            "
                        >
                            <Allocation
                                v-for="allocation in row.allocations"
                                v-bind="allocation"
                                :key="allocation.id"
                                :allocation-usages="usages"
                                :phase-type-priorities="phaseTypePriorities"
                            >
                                <template v-if="allocation.allocationType === 'Aggregation'" #tooltip-text>
                                    <div class="w-full text-center text-gray-500">
                                        {{ $t('aggregation-expand-hint') }}
                                    </div>
                                </template>
                            </Allocation>
                        </LayoutRow>
                    </template>
                </template>
            </Timeline>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useCreateAllocation } from '@/allocation/composables/useCreateAllocation';
import { useDeleteAllocation } from '@/allocation/composables/useDeleteAllocation';
import { useUpdateAllocations } from '@/allocation/composables/useUpdateAllocations';
import { useVariantSelectionPanel } from '@/components/AllocationVariantSelectionPanel/composables/useVariantSelectionPanel';
import { DEFAULT_HIGHLIGHT_VALUE } from '@/components/AllocationVariantSelectionPanel/types';
import Message from '@/components/Message/Message.vue';
import OptionSwitch from '@/components/OptionSwitch/OptionSwitch.vue';
import OptionSwitchEntry from '@/components/OptionSwitch/OptionSwitchEntry.vue';
import { useOptionSwitch } from '@/components/OptionSwitch/composables/useOptionSwitch';
import FederalStatesTooltip from '@/components/Timeline/FederalStatesTooltip.vue';
import InternationalTooltip from '@/components/Timeline/InternationalTooltip.vue';
import { DEFAULT_OPTION_SWITCH_ID, timelineOptionSwitchEntries } from '@/components/Timeline/Timeline.optionSwitch';
import { LocationTypeCategory } from '@/components/Timeline/Timeline.types';
import Timeline from '@/components/Timeline/Timeline.vue';
import Allocation from '@/components/Timeline/allocation/Allocation.vue';
import { AllocationPhaseInput } from '@/components/Timeline/allocation/types';
import AllocationFirstColumnCell from '@/components/Timeline/components/AllocationFirstColumnCell.vue';
import ExternalEventFirstColumnCell from '@/components/Timeline/components/ExternalEventFirstColumnCell.vue';
import LayoutRow from '@/components/Timeline/components/LayoutRow.vue';
import LocationAllocationCountLabel from '@/components/Timeline/components/LocationAllocationCountLabel.vue';
import ReferenceAllocationFirstColumnCell from '@/components/Timeline/components/ReferenceAllocationFirstColumnCell.vue';
import SectionAllocationCountLabel from '@/components/Timeline/components/SectionAllocationCountLabel.vue';
import SectionDaysRow from '@/components/Timeline/components/SectionDaysRow.vue';
import SectionFirstColumnCell from '@/components/Timeline/components/SectionFirstColumnCell.vue';
import { useAllocationsByLocation } from '@/components/Timeline/composables/useAllocationsByLocation';
import { useConflictMarkers } from '@/components/Timeline/composables/useConflictMarkers';
import { useCreateButtonStatus } from '@/components/Timeline/composables/useCreateButtonStatus';
import { useHierarchyRows } from '@/components/Timeline/composables/useHierarchyRows';
import { useSectionRows } from '@/components/Timeline/composables/useSectionRows';
import { useTimelineConflicts } from '@/components/Timeline/composables/useTimelineConflicts';
import { useTimelineLocations } from '@/components/Timeline/composables/useTimelineLocations';
import { useTimelineRange } from '@/components/Timeline/composables/useTimelineRange';
import { useTimelineSections } from '@/components/Timeline/composables/useTimelineSections';
import { useTimelineValidation } from '@/components/Timeline/composables/useTimelineValidation';
import { countConflicts } from '@/components/Timeline/logic/conflictCounts';
import TimelineIntervalChanger from '@/components/TimelineIntervalChanger/TimelineIntervalChanger.vue';
import {
    MAXIMAL_YEAR,
    MINIMAL_YEAR,
    useTimelineIntervalChanger,
} from '@/components/TimelineIntervalChanger/composables/useTimelineIntervalChanger';
import { useEventAllocations } from '@/event/composables/useEventAllocations';
import { useEventInjection } from '@/event/composables/useEventInjection';
import { useEventTimelineData } from '@/event/composables/useEventTimelineData';
import { useEventVariantSelectionDropdown } from '@/event/composables/useEventVariantSelectionDropdown';
import { useExternalEventAggregationRows } from '@/external_event/composables/useExternalEventAggregationRows';
import { DeleteEventAllocationAreaDocument, DeleteEventAllocationRoomDocument } from '@/generated/graphql';
import { cleanHighlight, cleanVisible } from '@/navigation/utils';
import { $t } from '@/plugins/fluent';
import { insideRangeCheck } from '@/utils/date';
import VariantSelectionDropdown from '@/variant/components/VariantSelectionDropdown.vue';
import { useHeadSafe } from '@unhead/vue';
import { useRouteQuery } from '@vueuse/router';
import { DateTime, QuarterNumbers } from 'luxon';
import { computed } from 'vue';

const props = defineProps<{ eventId: string }>();

const eventId = computed(() => props.eventId);
const allocationType = 'Event';
const phaseTypePriorities = [3, 5, 0, 8, 2, 6, 1, 7];

const visible = useRouteQuery<string>('visible', '');
const highlight = useRouteQuery<string>('highlight', DEFAULT_HIGHLIGHT_VALUE);
const timelineDisplay = useRouteQuery<string>('timeline-display', '');
const quarter = useRouteQuery<string>('quarter', '');
const year = useRouteQuery<string>('year', '');

const { refetchHasAllocations } = useEventInjection();

const {
    startDateTime,
    endDateTime,
    isTimeSpanModified,
    previousMonthButton,
    nextMonthButton,
    dateTimeFromRoute,
    onExpandToPreviousMonth,
    onExpandToNextMonth,
    addToStartDateTime,
    subtractFromStartDateTime,
    setSelectedStartDateTime,
} = useTimelineRange(quarter, year);

const {
    variantsRef,
    setVariantsRef,
    timelineLoading,
    error: timelineError,
    onResult: onTimelineResult,
    site,
    event,
    rooms,
    areas,
    timelineHeaderName,
    timelineHeaderLinkTarget,
    defaultPhaseTypeId,
    executionPhasePosition,
    usages,
    createAllocation,
    updateAllocationData,
    duplicateAllocation,
    duplicateAndUpdateAllocation,
} = useEventTimelineData(eventId, dateTimeFromRoute, startDateTime, endDateTime, setSelectedStartDateTime);

const {
    dropDownStatus,
    visibleVariantIds,
    defaultActiveVariants,
    editableVariant,
    highlightedVariantId,
    highlightedVariant,
    mappedCurrentVariant,
    selectionPanelVariants,
    setVisibleVariantsByIds,
    setVisibleVariantsByIndexes,
    setHighlightedVariantIdById,
    setHighlightedVariantIdByIndex,
    onDeleteFilter,
    updateVisibility,
} = useVariantSelectionPanel(variantsRef, setVariantsRef, allocationType, eventId, visible, highlight);

const { newVariantIndex, onCreateNewVariant, onCopyAllocations } = useEventVariantSelectionDropdown(
    eventId,
    variantsRef,
    setVariantsRef,
    updateVisibility,
    startDateTime,
    endDateTime,
    executionPhasePosition,
);

const { filteredAllocations, referenceAllocationFirstColumnCell, referenceAllocations } = useEventAllocations(
    variantsRef,
    visibleVariantIds,
    startDateTime,
    endDateTime,
);

const { conflictsByDay } = useTimelineConflicts(filteredAllocations, startDateTime, endDateTime);

const conflictMarkersByLocation = useConflictMarkers(filteredAllocations, startDateTime, endDateTime);

// Site-level conflict total for the header cell: the sum of every location's conflict spans.
// Resolved conflicts don't exist yet, so everything counts as unresolved for now.
const siteConflictsCount = computed(() => {
    let total = 0;
    for (const markers of conflictMarkersByLocation.value.values()) {
        total += countConflicts(markers);
    }
    return { resolved: 0, unresolved: total, total };
});

const { layoutRows, firstColumnCells } = useExternalEventAggregationRows(
    startDateTime,
    endDateTime,
    executionPhasePosition.value,
    undefined,
    undefined,
);

const referenceAllocationsLayoutRow = computed(() => ({
    timelineStartDateTime: startDateTime.value,
    timelineEndDateTime: endDateTime.value,
    rowId: 'reference-allocation-row',
    isFirstRow: true,
    layoutAlgorithm: 'Allocation-Variant-Order' as const,
    createButtonStatus: { type: 'Hidden' as const },
}));

const allocationsByLocation = useAllocationsByLocation(filteredAllocations);

// Event allocation layout rows per section with nested location rows
const eventAllocationLayoutRowsBySection = computed(() =>
    sections.value.map((section) => ({
        ...section,
        locationRows: section.locations.map((location) => ({
            rowId: `location-${location.id}`,
            isFirstRow: false,
            layoutAlgorithm: 'Compact' as const,
            createButtonStatus: highlightedVariant.value
                ? { type: 'Visible' as const, rowLabel: location.label, perDayStatus: getPerDayStatus(location.id) }
                : {
                      type: 'Disabled' as const,
                      rowLabel: location.label,
                      tooltipText: $t('hierarchy-no-variant-selected'),
                  },
            conflictMarkers: location.conflictMarkers,
            allocations: location.allocations,
            locationId: location.id,
            locationTypeCategory: location.typeCategory,
        })),
    })),
);

const visibleAllocationsCount = computed(() => {
    const start = startDateTime.value;
    const end = endDateTime.value;
    let allocationsInRange = filteredAllocations.value;
    if (start && end) {
        allocationsInRange = allocationsInRange.filter((allocation) =>
            insideRangeCheck(allocation.startDateTime, allocation.endDateTime, start, end),
        );
    }
    return allocationsInRange.length;
});

const { activeIndex, setActiveIndex, setActiveId } = useOptionSwitch(timelineDisplay, timelineOptionSwitchEntries);

const showSections = computed(() => timelineDisplay.value === 'sections');

onTimelineResult((result) => {
    if (result.data) {
        if (visible.value) {
            setVisibleVariantsByIndexes(cleanVisible(visible.value));
        } else {
            setVisibleVariantsByIds(defaultActiveVariants.value.map((v) => v.id));
        }
        if (highlight.value && highlight.value !== DEFAULT_HIGHLIGHT_VALUE) {
            setHighlightedVariantIdByIndex(cleanHighlight(highlight.value));
        }
        const displayId = timelineDisplay.value === '' ? DEFAULT_OPTION_SWITCH_ID : timelineDisplay.value;
        setActiveId(displayId);
    }
});

const { sections: baseSections, toggleSectionExpanded } = useTimelineSections();

const { onCreateAllocation } = useCreateAllocation(createAllocation, editableVariant);
const { onDeleteAllocation } = useDeleteAllocation(variantsRef, setVariantsRef);
const { updateAllocation } = useUpdateAllocations(
    updateAllocationData,
    duplicateAllocation,
    duplicateAndUpdateAllocation,
);

async function handleDeleteAllocation(
    allocationId: string,
    locationTypeCategory: LocationTypeCategory,
    onDone: (isSuccess: boolean) => void,
) {
    const deleteDocument =
        locationTypeCategory === 'Room' ? DeleteEventAllocationRoomDocument : DeleteEventAllocationAreaDocument;
    await onDeleteAllocation(allocationId, deleteDocument, onDone);
    refetchHasAllocations();
}

async function handleCreateAllocation(
    locationId: string,
    startDateTime: DateTime,
    endDateTime: DateTime,
    allocationVariantId: string,
    phases: AllocationPhaseInput[],
    locationTypeCategory: LocationTypeCategory,
    onDone: (isSuccess: boolean) => void,
) {
    await onCreateAllocation(
        locationId,
        startDateTime,
        endDateTime,
        allocationVariantId,
        phases,
        locationTypeCategory,
        onDone,
    );
    refetchHasAllocations();
}

const { warningNoAllocations, onChangeQuarter, onChangeYear, onClickPreviousQuarter, onClickNextQuarter } =
    useTimelineIntervalChanger(
        quarter,
        year,
        timelineLoading,
        visibleAllocationsCount,
        addToStartDateTime,
        subtractFromStartDateTime,
    );

const { getLocations } = useTimelineLocations(site, rooms, areas, showSections);
const locations = computed(() => getLocations().value);

const { hierarchyRows, toggleLocationExpanded } = useHierarchyRows({
    locations,
    allocationsByLocation,
    conflictMarkersByLocation,
    startDateTime,
    endDateTime,
    enabled: computed(() => !showSections.value),
});

const { sections } = useSectionRows({
    baseSections,
    locations,
    allocationsByLocation,
    conflictMarkersByLocation,
    startDateTime,
    endDateTime,
});

useTimelineValidation(locations, filteredAllocations);

useHeadSafe({
    title: () =>
        `${$t('overview')} - ${event.value?.nameShort ?? ''} - ${event.value?.series?.nameShort ?? ''} - ${$t(
            'event-wiki',
        )}`,
});

// Layout rows for hierarchy view
const hierarchyLayoutRows = computed(() =>
    hierarchyRows.value
        .filter((location) => location.isVisible)
        .map((location) => ({
            rowId: `location-${location.id}`,
            isFirstRow: false,
            layoutAlgorithm: 'Compact' as const,
            createButtonStatus: location.isLeafLocation
                ? highlightedVariant.value
                    ? { type: 'Visible' as const, rowLabel: location.label, perDayStatus: getPerDayStatus(location.id) }
                    : {
                          type: 'Disabled' as const,
                          rowLabel: location.label,
                          tooltipText: $t('hierarchy-no-variant-selected'),
                      }
                : { type: 'Hidden' as const },
            conflictMarkers: location.conflictMarkers,
            allocations: location.allocations,
            // Used by the create-allocation handler; not consumed by LayoutRow itself
            locationId: location.id,
            locationTypeCategory: location.typeCategory,
        })),
);

const { getPerDayStatus, handleRowCreate } = useCreateButtonStatus({
    onCreate: ({ locationId, locationTypeCategory, startDateTime, endDateTime }) => {
        if (!editableVariant.value) {
            return Promise.resolve(false);
        }
        const variantId = editableVariant.value.id;
        return new Promise<boolean>((resolve) => {
            handleCreateAllocation(
                locationId,
                startDateTime,
                endDateTime,
                variantId,
                [{ offset: { days: 0 }, duration: { days: 1 }, usageId: defaultPhaseTypeId.value }],
                locationTypeCategory,
                (isSuccess: boolean) => resolve(isSuccess),
            );
        });
    },
});
</script>
